import jsPDF from 'jspdf';

export class FooterMarginTop {
  marginTop: number;
  currentPage: number;
}
export abstract class PdfBuilder {

  private doc: jsPDF;
  private lineY = 0;
  private useTempDoc = false;
  private tempDoc: jsPDF;

  protected paddingLeftRight = 15;
  protected paddingTopBottom = 5;
  protected footerMarginTop = new Array<FooterMarginTop>();
  protected getIndexsHeader = new Array<number[]>();

  constructor() {
    this.doc = this.initDoc();
    this.tempDoc = this.initDoc();
  }

  protected abstract initDoc(): jsPDF;

  /**
   * Return: final lineY
   */
  protected abstract buildHeader(startY: number, pageIndex?: number): number;

  /**
   * Return: final lineY
   */
  protected abstract buildBody(startY: number): number;

  /**
   * Return: final lineY
   */
  protected abstract buildFooter(startY: number, pageIndex?: number): number;

  protected abstract getFileName(): string;

  buildPdf(): jsPDF {
    // 1. Build body first
    this.lineY = this.buildBody(this.paddingTopBottom + this.getHeight(this.buildHeader));
    // 2. Build header for each page
    const pageCount = this.getDoc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      this.getDoc.setPage(i);
      this.buildHeader(this.paddingTopBottom, i);
    }

    // 3. Build footer
    if (this.footerMarginTop.length > 0) {
      for (let i = 1; i <= pageCount; i++) {
        const footerMargin = this.footerMarginTop.find(x => x.currentPage === i);
        if (footerMargin) {
          this.setFooter(footerMargin.marginTop, i);
        }
      }
    } else {
      this.setFooter(this.lineY, pageCount);
    }
    this.getDoc.setProperties({ title: this.getFileName() });

    return this.getDoc;
  }

  protected get getDoc(): jsPDF {
    return this.useTempDoc ? this.tempDoc : this.doc;
  }

  protected textCenter(text: string, y, offsetX = 0, pageWidth = 0) {
    const fontSize = this.getDoc.getFontSize();
    pageWidth = pageWidth || this.getDoc.internal.pageSize.getWidth();
    const txtWidth = this.getDoc.getStringUnitWidth(text) * fontSize / this.getDoc.internal.scaleFactor;
    this.getDoc.text(text, (pageWidth - txtWidth) / 2 + offsetX, y);
  }

  protected textRight(text: string, y: number, marginRight = 0, pageWidth = 0) {
    const fontSize = this.getDoc.getFontSize();
    pageWidth = pageWidth || this.getDoc.internal.pageSize.getWidth();
    const txtWidth = this.getDoc.getStringUnitWidth(text) * fontSize / this.getDoc.internal.scaleFactor;
    this.getDoc.text(text, (pageWidth - txtWidth) - marginRight, y);
  }

  protected getHeight(buildBlock: (startY: number) => number): number {
    const originalLineY = this.lineY;
    this.useTempDoc = true;
    const height = buildBlock.apply(this, [this.lineY]) - originalLineY;
    this.useTempDoc = false;
    this.lineY = originalLineY;
    return height;
  }

  protected textMaxWidth(text: string, x: number, y: number, maxWidth: number, lineHeight: number, options: any): number {
    const textChunks = this.getDoc.splitTextToSize(text || '', maxWidth);
    this.getDoc.text(textChunks, x, y, options);
    return y + (textChunks.length - 1) * lineHeight;
  }

  private setFooter(startY: number, pageIndex: number) {
    this.getDoc.setPage(pageIndex);
    const footerHeight = this.getHeight(this.buildFooter);
    if (this.lineY + footerHeight > this.getDoc.internal.pageSize.getHeight() - this.paddingTopBottom) {
      this.getDoc.addPage();
      this.lineY = this.paddingTopBottom;
    }
    this.buildFooter(startY, pageIndex);
  }
}
